import { useEffect, useRef, useState } from 'react';
import { Icon } from '../../../layout/Icon.jsx';
import { useAuth } from '../../../shared/useAuth.js';
import { toast } from '../../../shared/useToast.js';

// ───── CSV ─────────────────────────────────────────────────────────────
function escapeCell(v) {
  if (v == null) return '';
  const s = String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function buildRows(rows, columns) {
  return rows.map((r) =>
    columns.map((c) => (typeof c.value === 'function' ? c.value(r) : r[c.key]))
  );
}

function toCSV(rows, columns) {
  if (!rows?.length) return '';
  const header = columns.map((c) => escapeCell(c.label)).join(',');
  const body = buildRows(rows, columns)
    .map((cells) => cells.map(escapeCell).join(','))
    .join('\n');
  return `\uFEFF${header}\n${body}`;
}

function downloadBlob(filename, blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function downloadCSV(filename, csv) {
  downloadBlob(filename, new Blob([csv], { type: 'text/csv;charset=utf-8' }));
}

// ───── Excel (XLSX) — تحميل كسول ─────────────────────────────────────
async function downloadExcel(filename, rows, columns) {
  const XLSX = await import('xlsx');
  const header = columns.map((c) => c.label);
  const body = buildRows(rows, columns);
  const ws = XLSX.utils.aoa_to_sheet([header, ...body]);
  ws['!rtl'] = true;
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'تقرير');
  XLSX.writeFile(wb, filename);
}

// ───── PDF ───────────────────────────────────────────────────────────
//
// ⚠️ لماذا لا نكتب الجدول بـ jsPDF/autoTable مباشرة:
//   خطوط jsPDF المدمجة (helvetica/times/courier) مُرمَّزة WinAnsi ولا
//   تحوي حرفاً عربياً واحداً، فكل حرف عربي كان يخرج محرفاً («þ» في تدفّق
//   المحتوى). النتيجة: ملف PDF صالح شكلاً لكنه طلاسم كاملة — العناوين
//   ورؤوس الأعمدة وأسماء المنتجات والكاشيرين. وحتى مع تضمين خط عربي، فإن
//   jsPDF لا يصل الحروف (contextual shaping) ولا يعكس اتجاه السطر (bidi)،
//   فيخرج النص مفكّكاً ومقلوباً.
//
//   الحل: نبني مستند HTML بالاتجاه RTL وبخط الواجهة نفسه، يرسمه المتصفح
//   (وهو يعرف التشكيل والاتجاه)، ثم نلتقطه بـ html2canvas ونقسّمه على
//   صفحات A4 داخل jsPDF. فائدة إضافية: الرسم البياني يدخل الملف كما هو،
//   والأرقام والعناوين مطابقة تماماً لما على الشاشة.
//
//   التقسيم يتم عند حدود الصفوف لا في منتصفها، ورأس الجدول يتكرّر في كل
//   صفحة، ولا تُضاف صفحة إلا إذا بقي محتوى فعلي (فلا صفحة بيضاء زائدة).

const PDF_FONT_STACK = "'Tajawal','Segoe UI','Noto Naskh Arabic',Tahoma,sans-serif";

function fmtGenerated(d) {
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

function esc(v) {
  return String(v == null ? '' : v)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** يبني عقدة المستند خارج الشاشة (لا تومض أمام المستخدم). */
function buildPdfNode({ title, subtitle, meta, rows, columns, totalsRow, chartDataUrl, widthPx }) {
  const node = document.createElement('div');
  node.setAttribute('dir', 'rtl');
  node.setAttribute('lang', 'ar');
  // خارج الشاشة بـ position:fixed — لا يضيف تمريراً للصفحة ولا يومض أمام
  // المستخدم، ويبقى مرئياً لـ html2canvas (visibility:hidden كان يُعطي
  // لوحة بيضاء فارغة).
  node.style.cssText = [
    'position:fixed',
    'top:0',
    'left:-20000px',
    `width:${widthPx}px`,
    'background:#ffffff',
    'color:#111827',
    `font-family:${PDF_FONT_STACK}`,
    'padding:0',
    'margin:0',
    'z-index:-1'
  ].join(';');

  const metaChips = (meta || [])
    .filter(Boolean)
    .map(
      (m) =>
        `<span style="display:inline-block;background:rgba(255,255,255,.22);border-radius:999px;padding:3px 10px;margin:2px 0 2px 6px;font-size:11.5px;font-weight:600">${esc(m)}</span>`
    )
    .join('');

  // الجداول العريضة (تقرير الخصومات: 15 عموداً) كانت تتجاوز عرض الصفحة
  // فيُقتطع آخر عمود تماماً. الخط يصغر مع عدد الأعمدة، ورؤوس الأعمدة تلتفّ
  // بدل `nowrap` الذي كان يفرض عرضاً أكبر من الصفحة.
  const n = columns.length;
  const fs = n > 12 ? 9 : n > 9 ? 10 : n > 6 ? 11 : 11.5;
  const pad = n > 9 ? '4px 5px' : '5px 8px';
  const cellBase = `font-size:${fs}px;padding:${pad};text-align:right;vertical-align:top;`
    + 'word-break:break-word;overflow-wrap:anywhere';

  const head = columns
    .map(
      (c) =>
        `<th style="border:1px solid #d1d5db;background:#FFF1E4;color:#7C2D12;font-weight:800;${cellBase}">${esc(c.label)}</th>`
    )
    .join('');

  const body = buildRows(rows, columns)
    .map(
      (cells, i) =>
        `<tr style="background:${i % 2 ? '#FBFAF7' : '#ffffff'}">${cells
          .map((v) => `<td style="border:1px solid #e5e7eb;${cellBase}">${esc(v)}</td>`)
          .join('')}</tr>`
    )
    .join('');

  const foot = Array.isArray(totalsRow) && totalsRow.length
    ? `<tfoot><tr style="background:#FFF7ED;font-weight:800">${columns
      .map((c, i) => {
        const cell = totalsRow[i];
        return `<td style="border:1px solid #d1d5db;${cellBase}">${esc(
          cell == null ? '' : cell
        )}</td>`;
      })
      .join('')}</tr></tfoot>`
    : '';

  node.innerHTML = `
    <div style="background:linear-gradient(135deg,#FF7A1A,#FFB627);color:#fff;padding:14px 16px;border-radius:10px;margin-bottom:12px">
      <div style="font-size:20px;font-weight:800;line-height:1.4">${esc(title || 'تقرير')}</div>
      ${subtitle ? `<div style="font-size:12px;opacity:.92;margin-top:2px">${esc(subtitle)}</div>` : ''}
      <div style="margin-top:6px">${metaChips}</div>
      <div style="font-size:11px;opacity:.9;margin-top:6px">أُصدر: ${esc(fmtGenerated(new Date()))} — عدد السجلات: ${rows.length}</div>
    </div>
    ${chartDataUrl ? `<div style="margin-bottom:12px;border:1px solid #e5e7eb;border-radius:10px;padding:8px;background:#fff"><img src="${chartDataUrl}" style="display:block;width:100%;height:auto" /></div>` : ''}
    <table style="width:100%;border-collapse:collapse;table-layout:auto">
      <thead><tr>${head}</tr></thead>
      <tbody>${body}</tbody>
      ${foot}
    </table>
  `;

  document.body.appendChild(node);

  // شبكة أمان: إن بقي الجدول أعرض من الصفحة رغم الالتفاف (أسماء طويلة بلا
  // مسافات) نُجبر توزيعاً ثابتاً للأعمدة — عرض أضيق أفضل من عمود مقطوع.
  const table = node.querySelector('table');
  if (table && table.scrollWidth > node.clientWidth + 1) {
    table.style.tableLayout = 'fixed';
  }
  return node;
}

/** قصّ شريحة من اللوحة إلى لوحة جديدة بخلفية بيضاء. */
function sliceCanvas(source, top, height) {
  const out = document.createElement('canvas');
  out.width = source.width;
  out.height = Math.max(1, Math.round(height));
  const ctx = out.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, out.width, out.height);
  ctx.drawImage(source, 0, top, source.width, height, 0, 0, source.width, height);
  return out;
}

async function downloadPDF(filename, rows, columns, opts = {}) {
  const [{ jsPDF }, html2canvasMod] = await Promise.all([
    import('jspdf'),
    import('html2canvas')
  ]);
  const html2canvas = html2canvasMod.default || html2canvasMod;

  // الاتجاه: الجداول العريضة أفقية حتى لا يُقتطع أي عمود.
  const orientation = opts.orientation || (columns.length > 6 ? 'landscape' : 'portrait');
  // عرض اللوحة يكبر مع عدد الأعمدة: الصفحة واحدة دائماً (A4) فالمحصّلة خط
  // أدقّ لا عمود مقطوع.
  const widthPx = orientation === 'landscape'
    ? (columns.length > 10 ? 1500 : 1100)
    : 780;

  // الرسم البياني أولاً (إن وُجد) ليدخل داخل المستند نفسه.
  let chartDataUrl = null;
  if (opts.chartEl) {
    try {
      const c = await html2canvas(opts.chartEl, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false,
        // الأسماء المقصوصة (`truncate` = overflow:hidden + ellipsis) كانت
        // تخرج في الصورة نصف مقطوعة لأن النسخة المستنسخة تحسب عرضاً أضيق
        // قليلاً. في الورق لا داعي للقصّ أصلاً — نفكّه على النسخة فقط.
        onclone: (_doc, el) => {
          if (!el || !el.querySelectorAll) return;
          el.querySelectorAll('*').forEach((n) => {
            const cs = n.ownerDocument.defaultView.getComputedStyle(n);
            if (cs.textOverflow === 'ellipsis' || cs.overflow === 'hidden') {
              n.style.textOverflow = 'clip';
              n.style.overflow = 'visible';
            }
          });
        }
      });
      if (c.width && c.height) chartDataUrl = c.toDataURL('image/png');
    } catch (_) {
      chartDataUrl = null; // رسم يتعذّر التقاطه لا يُسقط التصدير كله
    }
  }

  if (document.fonts && document.fonts.ready) {
    try { await document.fonts.ready; } catch (_) { /* ignore */ }
  }

  const node = buildPdfNode({ ...opts, rows, columns, chartDataUrl, widthPx });

  let canvas;
  let theadStrip = null;
  let boundaries = [];
  let ratio = 2;
  try {
    // دقّة أعلى للجداول القصيرة، وأقل للطويلة جداً حتى لا تنفجر الذاكرة.
    // المتصفح يُعيد لوحة فارغة بلا خطأ إذا تجاوزت مساحتها حدّه، وتقرير بمئات
    // الأسطر يبلغه بسهولة — فنخفض الدقّة بدل أن يخرج ملف أبيض.
    let scale = node.offsetHeight > 6000 ? 1.5 : 2;
    const area = node.offsetWidth * node.offsetHeight;
    const MAX_PX = 45e6;
    if (area * scale * scale > MAX_PX) {
      scale = Math.max(0.6, Math.sqrt(MAX_PX / Math.max(1, area)));
    }
    canvas = await html2canvas(node, {
      backgroundColor: '#ffffff',
      scale,
      logging: false,
      useCORS: true
    });
    ratio = canvas.width / node.offsetWidth;

    const nodeTop = node.getBoundingClientRect().top;
    const theadEl = node.querySelector('thead');
    if (theadEl) {
      const r = theadEl.getBoundingClientRect();
      const top = Math.max(0, Math.round((r.top - nodeTop) * ratio));
      const h = Math.round(r.height * ratio);
      if (h > 0) theadStrip = sliceCanvas(canvas, top, h);
    }
    // حدود القصّ = أسفل كل صف، فلا يُشطر صف بين صفحتين.
    boundaries = Array.from(node.querySelectorAll('tbody tr, tfoot tr')).map((tr) =>
      Math.round((tr.getBoundingClientRect().bottom - nodeTop) * ratio)
    );
  } finally {
    node.remove();
  }

  const pdf = new jsPDF({ orientation, unit: 'pt', format: 'a4' });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const margin = 20;
  const contentW = pageW - margin * 2;
  const contentH = pageH - margin * 2;
  const pxPerPt = canvas.width / contentW;
  const maxSlicePx = contentH * pxPerPt;

  let cursor = 0;
  let page = 0;
  // الحدّ `-2` يمنع صفحة أخيرة فارغة بسبب كسر تقريب بكسل واحد.
  while (cursor < canvas.height - 2) {
    const headPx = page > 0 && theadStrip ? theadStrip.height : 0;
    const available = maxSlicePx - headPx;
    let end = Math.min(canvas.height, cursor + available);
    if (end < canvas.height) {
      const snapped = boundaries.filter((b) => b > cursor + 8 && b <= end).pop();
      if (snapped) end = snapped;
    }
    // صف أطول من صفحة كاملة: نقصّه قصّاً صلباً بدل الدوران إلى ما لا نهاية.
    if (end <= cursor) end = Math.min(canvas.height, cursor + available);

    const bodyH = end - cursor;
    const chunk = document.createElement('canvas');
    chunk.width = canvas.width;
    chunk.height = Math.round(headPx + bodyH);
    const ctx = chunk.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, chunk.width, chunk.height);
    if (headPx) ctx.drawImage(theadStrip, 0, 0);
    ctx.drawImage(canvas, 0, cursor, canvas.width, bodyH, 0, headPx, canvas.width, bodyH);

    if (page > 0) pdf.addPage('a4', orientation);
    // JPEG لا PNG: صفحة بعرض 2200 بكسل فيها تدرّج لوني تُنتج PNG بحجم
    // ~8 ميغابايت للصفحة الواحدة (ملف 15 ميغا لتقرير من صفحتين — غير صالح
    // للإرسال أو الطباعة). JPEG بجودة 0.92 يعطي عُشر الحجم والنص يبقى
    // واضحاً تماماً عند هذه الدقّة.
    pdf.addImage(
      chunk.toDataURL('image/jpeg', 0.92),
      'JPEG',
      margin,
      margin,
      contentW,
      chunk.height / pxPerPt
    );

    cursor = end;
    page += 1;
    if (page > 200) break; // حارس أمان
  }

  pdf.save(filename);
}

// ───── Image (PNG) — يلتقط عنصر DOM كصورة ────────────────────────────
async function downloadImage(filename, element) {
  if (!element) {
    toast.error('لا يوجد عنصر لالتقاطه');
    return;
  }
  const html2canvas = (await import('html2canvas')).default;
  const canvas = await html2canvas(element, { backgroundColor: '#ffffff', scale: 2 });
  canvas.toBlob((blob) => {
    if (blob) downloadBlob(filename, blob);
  }, 'image/png');
}

// ───── المكوّن ────────────────────────────────────────────────────────
export function ExportButton({
  rows,
  columns,
  filename = 'export',
  title,
  subtitle,
  meta,
  totalsRow,
  orientation,
  tableRef,
  chartRef
}) {
  const hasPermission = useAuth((s) => s.hasPermission);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    function onClickAway(e) {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', onClickAway);
    return () => document.removeEventListener('mousedown', onClickAway);
  }, [open]);

  if (!hasPermission('reports.export')) return null;

  const baseName = filename.replace(/\.(csv|xlsx|pdf|png)$/i, '');
  const hasData = Array.isArray(rows) && rows.length > 0;

  async function run(format) {
    setOpen(false);
    try {
      if (format === 'csv') {
        if (!hasData) return toast.error('لا توجد بيانات للتصدير');
        downloadCSV(`${baseName}.csv`, toCSV(rows, columns));
        return toast.success('تم تصدير CSV');
      }
      if (format === 'xlsx') {
        if (!hasData) return toast.error('لا توجد بيانات للتصدير');
        await downloadExcel(`${baseName}.xlsx`, rows, columns);
        return toast.success('تم تصدير Excel');
      }
      if (format === 'pdf') {
        if (!hasData) return toast.error('لا توجد بيانات للتصدير');
        setBusy(true);
        try {
          await downloadPDF(`${baseName}.pdf`, rows, columns, {
            title,
            subtitle,
            meta,
            totalsRow,
            orientation,
            chartEl: chartRef?.current || null
          });
        } finally {
          setBusy(false);
        }
        return toast.success('تم تصدير PDF');
      }
      if (format === 'image-table') {
        await downloadImage(`${baseName}-table.png`, tableRef?.current);
        return toast.success('تم تصدير الجدول كصورة');
      }
      if (format === 'image-chart') {
        await downloadImage(`${baseName}-chart.png`, chartRef?.current);
        return toast.success('تم تصدير الرسم البياني كصورة');
      }
      return undefined;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('export error:', err);
      setBusy(false);
      return toast.error('تعذّر التصدير');
    }
  }

  const items = [
    { key: 'csv', label: 'CSV', icon: 'reports' },
    { key: 'xlsx', label: 'Excel', icon: 'reports' },
    { key: 'pdf', label: 'PDF', icon: 'print' },
    tableRef ? { key: 'image-table', label: 'صورة (الجدول)', icon: 'camera' } : null,
    chartRef ? { key: 'image-chart', label: 'صورة (الرسم البياني)', icon: 'camera' } : null
  ].filter(Boolean);

  return (
    <div className="relative no-print" ref={wrapRef}>
      <button
        type="button"
        className="btn"
        data-testid="export-button"
        disabled={busy}
        onClick={() => setOpen((v) => !v)}
      >
        <Icon name="reports" size={14} /> {busy ? 'جارٍ التصدير…' : 'تصدير'}
      </button>
      {open ? (
        <div
          className="absolute z-30 mt-1 right-0 min-w-[180px] bg-white border border-line rounded-[10px] shadow-card overflow-hidden"
        >
          {items.map((it) => (
            <button
              key={it.key}
              type="button"
              data-testid={`export-${it.key}`}
              className="w-full flex items-center gap-2 px-3 py-2 text-[12.5px] hover:bg-paper-2 text-right"
              onClick={() => run(it.key)}
            >
              <Icon name={it.icon} size={14} />
              <span>{it.label}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
